import { charValueAt, stripDocumentSeparators } from './internal';

/** CPF canônico: 11 dígitos, sem máscara. */
export const CPF_PATTERN = /^[0-9]{11}$/;
export const CPF_LENGTH = 11;

export type CpfErrorCode =
  | 'empty'
  | 'invalid-length'
  | 'invalid-characters'
  | 'repeated-digits'
  | 'check-digit-mismatch';

export interface CpfValidation {
  readonly valid: boolean;
  readonly canonical: string | null;
  readonly error: CpfErrorCode | null;
}

const REPEATED_DIGITS = /^([0-9])\1{10}$/;

function checkDigit(digits: string, length: number, startWeight: number): number {
  let sum = 0;
  for (let index = 0; index < length; index += 1) {
    sum += charValueAt(digits, index) * (startWeight - index);
  }
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

/** Os dois dígitos verificadores a partir dos nove primeiros; `null` se a base não tem nove dígitos. */
export function computeCpfCheckDigits(base: string): string | null {
  if (!/^[0-9]{9}$/.test(base)) return null;
  const first = checkDigit(base, 9, 10);
  const second = checkDigit(`${base}${String(first)}`, 10, 11);
  return `${String(first)}${String(second)}`;
}

/** Forma canônica (11 dígitos) ou `null`. Só forma: não confere dígito verificador. */
export function canonicalizeCpf(input: string): string | null {
  const stripped = stripDocumentSeparators(input);
  return CPF_PATTERN.test(stripped) ? stripped : null;
}

/** Valida e canonicaliza de uma vez; rejeita os onze dígitos iguais. */
export function validateCpf(input: string): CpfValidation {
  const stripped = stripDocumentSeparators(input);
  if (stripped.length === 0) return { valid: false, canonical: null, error: 'empty' };
  if (stripped.length !== CPF_LENGTH) return { valid: false, canonical: null, error: 'invalid-length' };
  if (!CPF_PATTERN.test(stripped)) return { valid: false, canonical: null, error: 'invalid-characters' };
  if (REPEATED_DIGITS.test(stripped)) return { valid: false, canonical: null, error: 'repeated-digits' };
  const expected = computeCpfCheckDigits(stripped.slice(0, 9));
  if (expected === null || stripped.slice(9) !== expected) {
    return { valid: false, canonical: null, error: 'check-digit-mismatch' };
  }
  return { valid: true, canonical: stripped, error: null };
}

export function isValidCpf(input: string): boolean {
  return validateCpf(input).valid;
}

/** `529.982.247-25` para exibição, ou `null` se o CPF é inválido. Nunca vai para o fio. */
export function formatCpf(input: string): string | null {
  const { canonical } = validateCpf(input);
  if (canonical === null) return null;
  return `${canonical.slice(0, 3)}.${canonical.slice(3, 6)}.${canonical.slice(6, 9)}-${canonical.slice(9)}`;
}
